import React from 'react';
import { motion } from 'framer-motion';
import { Reveal, StaggerContainer } from '../animations/Reveal';
import {
    FaChartLine,
    FaUserCheck,
    FaAward,
    FaStar,
    FaLeaf,
    FaHeart
} from 'react-icons/fa';
import '../../styles/globals.css';

const ResultsBit = ({ concerns, skinTypes }) => {
    const clinicalResults = [
        { metric: "94%", label: "Reported Brighter Skin", icon: FaChartLine },
        { metric: "87%", label: "Improved Skin Texture", icon: FaUserCheck },
        { metric: "91%", label: "Reduced Dullness", icon: FaAward },
        { metric: "4.9★", label: "Customer Satisfaction", icon: FaStar }
    ];

    return (
        <div className="space-y-16">
            {/* Header */}
            <div className="text-center space-y-4">
                <Reveal>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-sage/10 rounded-full">
                        <FaChartLine className="w-3 h-3 text-sage" />
                        <span className="text-sm tracking-widest uppercase text-sage">
              Visible Transformation
            </span>
                    </div>
                </Reveal>

                <Reveal delay={0.2}>
                    <h2 className="text-4xl md:text-5xl font-heading font-light">
                        Science-Backed <span className="italic text-champagne">Results</span> You Can{' '}
                        <span className="italic text-sage">Feel</span>
                    </h2>
                </Reveal>

                <Reveal delay={0.4}>
                    <p className="text-xl text-charcoal/60 max-w-2xl mx-auto">
                        Backed by clinical studies and loved by thousands worldwide
                    </p>
                </Reveal>
            </div>

            {/* Clinical Results */}
            <Reveal>
                <div className="bg-gradient-to-br from-white to-pearl rounded-3xl p-8 md:p-12 shadow-subtle">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
                        {clinicalResults.map((result, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                whileHover={{ y: -5 }}
                                className="text-center group"
                            >
                                <div className="inline-flex w-20 h-20 rounded-2xl bg-gradient-to-br from-champagne/10 to-sage/10 items-center justify-center mb-6 group-hover:from-champagne/20 group-hover:to-sage/20 transition-all duration-300">
                                    <result.icon className="w-8 h-8 text-champagne" />
                                </div>
                                <div className="text-3xl font-light text-charcoal mb-2">
                                    {result.metric}
                                </div>
                                <div className="text-charcoal/60">
                                    {result.label}
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Study Note */}
                    <div className="mt-12 pt-12 border-t border-nude/50 text-center">
                        <p className="text-charcoal/50 italic">
                            *Based on 4-week independent clinical study of 100 participants
                        </p>
                    </div>
                </div>
            </Reveal>

            {/* Concerns & Skin Types */}
            <div className="grid lg:grid-cols-2 gap-12">
                {/* Targeted Concerns */}
                <Reveal delay={0.2}>
                    <div className="space-y-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-champagne/10 flex items-center justify-center">
                                <FaHeart className="w-5 h-5 text-champagne" />
                            </div>
                            <h3 className="text-2xl font-light text-charcoal">Addresses</h3>
                        </div>

                        <StaggerContainer stagger={0.05}>
                            <div className="grid grid-cols-2 gap-4">
                                {concerns.map((concern, index) => (
                                    <motion.div
                                        key={index}
                                        variants={{
                                            hidden: { opacity: 0, scale: 0.9 },
                                            visible: { opacity: 1, scale: 1 }
                                        }}
                                        whileHover={{ scale: 1.05 }}
                                        className="bg-white rounded-xl p-6 shadow-subtle hover:shadow-soft transition-all duration-300"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center">
                                                <FaLeaf className="w-4 h-4 text-sage" />
                                            </div>
                                            <span className="font-medium text-charcoal">{concern}</span>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </StaggerContainer>
                    </div>
                </Reveal>

                {/* Suitable For */}
                <Reveal delay={0.4}>
                    <div className="space-y-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center">
                                <FaUserCheck className="w-5 h-5 text-sage" />
                            </div>
                            <h3 className="text-2xl font-light text-charcoal">Suitable For</h3>
                        </div>

                        <div className="bg-gradient-to-br from-white to-pearl rounded-2xl p-8 shadow-subtle">
                            <div className="space-y-4">
                                {skinTypes.map((type, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        className="flex items-center gap-4 p-4 rounded-xl bg-white/50"
                                    >
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-champagne/10 to-sage/10 flex items-center justify-center">
                                            <FaUserCheck className="w-5 h-5 text-champagne" />
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-charcoal">{type}</h4>
                                            <p className="text-sm text-charcoal/60">
                                                {type === "All Skin Types" && "Gentle formula suitable for everyone"}
                                                {type === "Sensitive Skin" && "Hypoallergenic and non-irritating"}
                                                {type === "Mature Skin" && "Targets signs of aging effectively"}
                                            </p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            <div className="mt-8 pt-8 border-t border-nude/50">
                                <p className="text-sm text-charcoal/50">
                                    Patch test recommended for those with extremely sensitive skin
                                </p>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>

            {/* Transformation Timeline */}
            <Reveal delay={0.6}>
                <div className="text-center pt-12">
                    <div className="inline-block p-8 bg-gradient-to-br from-white to-pearl rounded-2xl shadow-subtle">
                        <h4 className="text-xl font-medium text-charcoal mb-6">
                            Your Transformation Timeline
                        </h4>
                        <div className="flex flex-wrap justify-center gap-8">
                            {[
                                { week: "Week 1-2", result: "Enhanced hydration, smoother texture" },
                                { week: "Week 3-4", result: "Visible radiance, even tone" },
                                { week: "Week 5+", result: "Long-term repair, sustained glow" }
                            ].map((stage, index) => (
                                <div key={index} className="text-center">
                                    <div className="text-lg font-light text-champagne mb-2">{stage.week}</div>
                                    <div className="text-sm text-charcoal/60 max-w-[200px]">{stage.result}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </Reveal>
        </div>
    );
};

export default ResultsBit;